// ProcessNavigationMenu.tsx
import React, { useState } from "react";
import {
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from "@mui/material";
import {
    ArrowBackIosRounded,
    ArrowForwardIosRounded,
    Done,
    MoreVert,
    Redo,
    Undo,
} from "@mui/icons-material";
import { useTranslations } from "hooks";
import { getTranslation } from "common";
import { Task, SequenceFlow } from "model/activitiesModel";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";

interface ProcessNavigationMenuProps {
    task?: Task;
    handleNextStep?: (task: Task) => void;
    handleBackStep?: (task: Task) => void;
    handleSequenceFlowChoice?: (task: Task, sequenceFlow: SequenceFlow) => void;
    nextButtonLabel?: string;
    undoButtonLabel?: string;
    onClose: () => void;
}

const ProcessNavigationMenu: React.FC<ProcessNavigationMenuProps> = ({
    task,
    handleNextStep,
    handleBackStep,
    handleSequenceFlowChoice,
    nextButtonLabel,
    undoButtonLabel,
    onClose,
}) => {
    const translations = useTranslations();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleBack = () => {
        if (handleBackStep && task) {
            handleBackStep(task);
            onClose();
        }
        handleMenuClose();
    };

    const handleNext = () => {
        if (handleNextStep && task) {
            handleNextStep(task);
            onClose();
        }
        handleMenuClose();
    };

    const handleSequenceFlowClick = (sequenceFlow: SequenceFlow) => {
        if (handleSequenceFlowChoice && task) {
            handleSequenceFlowChoice(task, sequenceFlow);
            onClose();
        }
        handleMenuClose();
    };

    // Non mostrare nulla se non ci sono azioni disponibili
    if (!handleNextStep && !handleBackStep && !task?.sequenceflows) {
        return null;
    }

    const hasBackAction = handleBackStep && task?.state === 1;
    const hasNextAction = handleNextStep && (!task?.sequenceflows || task.sequenceflows?.length <= 0);
    const hasSequenceFlows = task?.sequenceflows && task.sequenceflows.length > 0 && handleSequenceFlowChoice;

    // Se non ci sono azioni da mostrare, non renderizzare nulla
    if (!hasBackAction && !hasNextAction && !hasSequenceFlows) {
        return null;
    }

    return (
        <>
            <FlowentiButton
                id="process-navigation-menu"
                aria-controls={menuOpen ? "process-navigation-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? "true" : undefined}
                onClick={handleClick}
                size="small"
                variant="outlined"
                startIcon={<MoreVert color="primary"
                    sx={{ marginRight: -1 }}
                />}
            >
                {getTranslation(translations, "generic.actions")}
            </FlowentiButton>

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{ "aria-labelledby": "process-navigation-button" }}
            >
                {/* Sequence Flows come opzioni di menu */}
                {hasSequenceFlows && task.sequenceflows!.map((sequenceFlow) => (
                    <MenuItem
                        key={`sequence-flow-${sequenceFlow.id}`}
                        onClick={() => handleSequenceFlowClick(sequenceFlow)}
                    >
                        <ListItemIcon>
                            <ArrowForwardIosRounded fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            {sequenceFlow.name}
                        </ListItemText>
                    </MenuItem>
                ))}

                {/* Divider se ci sono sia sequence flows che altre azioni */}
                {hasSequenceFlows && (hasNextAction || hasBackAction) && <Divider />}

                {/* Next action standard */}
                {hasNextAction && (
                    <MenuItem id={`process-next-button-${task?.idprocesstoken}`} onClick={handleNext}>
                        <ListItemIcon
                        >
                            {task?.state === 1 ? <Done fontSize="small" /> : <Redo fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText>
                            {getTranslation(
                                translations,
                                task?.state === 1 ? "process.action.done" : "process.action.next"
                            )}
                        </ListItemText>
                    </MenuItem>
                )}
                {hasBackAction && (
                    <MenuItem onClick={handleBack}>
                        <ListItemIcon>
                            <Undo fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            {undoButtonLabel || getTranslation(translations, "process.action.undo")}
                        </ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default ProcessNavigationMenu;